import { type IListPageChildProps } from "layouts/ListPageLayout";
import { useContext, useState } from "react";
import { Redirect, type RouteComponentProps } from "react-router";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type ILeadPageRedirectState } from "utilities/flatRoutes/lead.routes";
import { usePreviousLocation } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";
import { isDeepLinkEntry } from "utilities/routing/isDeepLinkEntry";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import PageTitle from "commonComponents/utilities/PageTitle/PageTitle";

import { type LeadTabNames } from "entity/lead/Lead/Lead.api.types";

const Lead = lazyLoadComponent(() => import("entity/lead/Lead/Lead"));

export interface ILeadFullPageRouteParams {
    leadId: string;
}

export function LeadFullPage(
    _listProps: IListPageChildProps,
    routeProps: RouteComponentProps<ILeadFullPageRouteParams>,
) {
    return (
        <>
            <PageTitle title="Lead Opportunity" />
            <WrappedPage routeProps={routeProps} />
        </>
    );
}

interface IWrappedPageProps {
    routeProps: RouteComponentProps<ILeadFullPageRouteParams>;
}

function WrappedPage({ routeProps }: IWrappedPageProps) {
    const { location, history } = routeProps;
    const builderInfo = useContext(BuilderInfoContext);
    // Captured on mount: a post-save replace flips history.action, which would make a deep link
    // look like an in-app entry and send goBack() out of the app.
    const previous = usePreviousLocation();
    const [openedByDeepLink] = useState(
        () =>
            isDeepLinkEntry(history, location) ||
            (location.state as ILeadPageRedirectState | undefined)?.openedByDeepLink === true,
    );
    const leadId = parseInt(routeProps.match.params.leadId);
    const query = new URLSearchParams(location.search);
    const selectedTab = (query.get("selectedTab") ?? undefined) as LeadTabNames | undefined;
    const showRelatedJob = query.get("showRelatedJob") !== "false";
    const showAddressMap = query.get("showAddressMap") !== "false";

    if (!builderInfo?.flags.leadsFullPageConversion) {
        return (
            <Redirect
                to={
                    routes.lead.getLeadListLink() +
                    routes.lead.getDetailsLink(leadId) +
                    location.search
                }
            />
        );
    }

    const handleBack = () => {
        if (openedByDeepLink && !previous) {
            history.replace(routes.lead.getLeadListLink());
            return;
        }
        history.goBack();
    };

    const modalConfig: IModalConfiguration = {
        parentRoute: routes.lead.getLeadListLink(),
        beforeClose: handleBack,
    };

    return (
        <Lead
            {...routeProps}
            modalConfig={modalConfig}
            leadId={leadId}
            selectedTab={selectedTab}
            showRelatedJob={showRelatedJob}
            showAddressMap={showAddressMap}
            onBack={handleBack}
        />
    );
}
